import {sharedCampaigns} from '@cl/shared';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {feedIdPrefixes} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.consts';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';
import conditionsApi from 'common/src/modules/conditions/conditions.api';
import {
  ContentFeedNode,
  DTNode,
  DTProductSelectionNode,
  DTStoreNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import {ConditionCardScratchpadState} from '../../../../standaloneAgents/promptToCardAgentV2/scratchpad/conditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const MAX_RESULTS = 120;

const searchFeedValuesToolSchema = z.object({
  cardId: z.string().describe(describeIdFormat(IdPrefix.Card)),
  feedIds: z
    .array(z.string().describe(describeIdFormat(feedIdPrefixes)))
    .optional()
    .nullable()
    .describe(
      'Optional. If provided, values are validated and used. If omitted, the tool searches using waterfall logic.',
    ),
  valueContainsString: z
    .string()
    .optional()
    .nullable()
    .describe('Optional filter string to search for values that contain this substring'),
  maxResults: z
    .number()
    .optional()
    .nullable()
    .default(MAX_RESULTS)
    .describe('Optional maximum number of results to return'),
});

function getFeedIdFromNode(node: DTNode): number | null {
  switch (node.type) {
    case NodeType.PRODUCTS: {
      const productNode = node as DTProductSelectionNode;

      if (productNode.feedId) {
        return productNode.feedId;
      }

      break;
    }

    case NodeType.STORE: {
      const storeNode = node as DTStoreNode;

      if (storeNode.locationFeedId) {
        return storeNode.locationFeedId;
      }

      break;
    }

    case NodeType.CONTENT_FEED: {
      const contentNode = node as ContentFeedNode;

      if (contentNode.contentFeedId) {
        return contentNode.contentFeedId;
      }

      break;
    }
  }

  return null;
}

function getFeedsFromEntireCampaign(): number[] {
  const nodes = sharedCampaigns.nodesRef.value;

  return Object.values(nodes)
    .map(getFeedIdFromNode)
    .filter((feedId) => feedId !== null);
}

function getFeedsFromPreviousCards(nodeId: number): number[] {
  const nodes = sharedCampaigns.nodesRef.value;
  const node = nodes[nodeId];

  if (!node || !node.inputs) {
    return [];
  }

  const feedIds: number[] = [];

  for (const input of node.inputs) {
    if (input.node) {
      const feedId = getFeedIdFromNode(input.node);

      if (feedId) {
        feedIds.push(feedId);
      }
    }
  }

  return feedIds;
}

/**
 * Parses a feed ID string that may have PF/LF/CF prefix.
 * Returns an error message if the prefix is for a non-feed entity.
 */
function parseFeedId(
  rawFeedId: string,
): {numericId: number; error?: undefined} | {numericId?: undefined; error: string} {
  const result = idHelper.parseFeedId(rawFeedId);

  if (result.ok === false) {
    return {error: formatParseError(result.error)};
  }

  return {numericId: result.numericId};
}

export default function searchFeedValuesTool() {
  async function execute({
    input: {cardId, feedIds = [], valueContainsString, maxResults},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof searchFeedValuesToolSchema,
    ConditionCardScratchpadState
  >): Promise<ToolResult> {
    try {
      const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
        agentScratchpad,
        NodeType.CONDITION,
      );

      if (!validatedScratchpad.card.shouldQueryOptionalValues) {
        return toolError(
          `Searching for values is not supported for signal: ${validatedScratchpad.card.signalDescription}.`,
        );
      }

      const cardIdParseResult = idHelper.parseCardId(cardId);

      if (cardIdParseResult.ok === false) {
        return toolError(formatParseError(cardIdParseResult.error));
      }

      const numericCardId = cardIdParseResult.numericId;
      const nodes = sharedCampaigns.nodesRef.value;

      const allFeeds = Object.values(nodes)
        .map(getFeedIdFromNode)
        .filter((feedId) => feedId !== null);

      let validatedFeedIds: number[] = [];

      if (!!feedIds?.length) {
        for (const rawFeedId of feedIds) {
          const parsed = parseFeedId(rawFeedId);

          if (parsed.error !== undefined) {
            return toolError(parsed.error);
          }

          if (allFeeds.includes(parsed.numericId)) {
            validatedFeedIds.push(parsed.numericId);
          }
        }
      } else {
        validatedFeedIds = getFeedsFromPreviousCards(numericCardId);

        if (feedIds?.length === 0) {
          validatedFeedIds = getFeedsFromEntireCampaign();
        }
      }

      const result = await conditionsApi.searchFeedValues({
        feedIds: validatedFeedIds,
        signalKey: validatedScratchpad.card.condKey,
        valueContainsString: valueContainsString ?? undefined,
        maxResults: maxResults || MAX_RESULTS,
      });

      return toolResult(
        JSON.stringify({
          values: result.values,
          hasMore: result.hasMore,
          instructions: result.hasMore
            ? 'More values are available. Consider refining the search with valueContainsString if needed.'
            : '',
        }),
      );
    } catch (error) {
      return toolError(
        `Error searching feed values: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.SearchFeedValues,
    displayName: 'Searching feed values',
    description:
      'Searches for values of a specific column (signal) from a feed. Use this tool when signal.shouldQueryOptionalValues is true and you need to find relevant values for condition outputs.',
    execute,
    inputSchema: searchFeedValuesToolSchema,
  });
}
